import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { SetInitialStateFacade } from '../../../../../common/models';
import { AllocationViewActionIdentifierTypeEnum } from '../constants';
import { ActionBriefModel, AllocationViewLeadUliModel, AllocationViewSelectedEntityModel } from '../models';
import { AllocationViewActionIdentifierModel } from '../models/allocation-view-action-request.model';
import {
  AllocationViewActionsCloseActionContainer,
  AllocationViewActionsInitialState,
  AllocationViewSetActionIdentifier,
  AllocationViewSetLeadUlis,
} from './allocation-view-actions.actions';
import { AllocationViewActionsState } from './allocation-view-actions.state';
import { AllocationViewActionMediatorService } from './allocation-view-action-mediator-service';

@Injectable({ providedIn: 'root' })
export class AllocationViewActionsFacade implements SetInitialStateFacade {
  actionIdentifier$: Observable<AllocationViewSelectedEntityModel> = this.store.select(
    AllocationViewActionsState.getActionIdentifier,
  );
  closeActionContainer$: Observable<boolean> = this.store.select(AllocationViewActionsState.getCloseActionContainer);
  shouldUpdate$: Observable<boolean> = this.store.select(AllocationViewActionsState.getShouldUpdate);
  leadUlis$: Observable<AllocationViewLeadUliModel[]> = this.store.select(AllocationViewActionsState.getLeadUlis);
  loaded$: Observable<boolean> = this.store.select(AllocationViewActionsState.getLoaded);

  private _actionCompleted$: Subject<void> = new Subject<void>();
  actionCompleted$: Observable<void> = this._actionCompleted$.asObservable();

  constructor(private store: Store, private mediator: AllocationViewActionMediatorService) {}

  setLeadUlis(leadUlis: AllocationViewLeadUliModel[]): void {
    this.store.dispatch(new AllocationViewSetLeadUlis(leadUlis));
  }

  setActionIdentifier(actionBrief: ActionBriefModel): void {
    const currentTruckId = this.mediator.getCurrentTruckId();
    this.mediator.clear();
    let identifier: AllocationViewActionIdentifierModel = {
      hubId: actionBrief.hubId,
      truckId: currentTruckId ?? actionBrief.truckId,
      ulis: [],
      slot: null,
    };

    if (actionBrief.actionOrigin === AllocationViewActionIdentifierTypeEnum.Uli) {
      const { uli, type, compartmentId, allocatedCompartmentId, slotId } = actionBrief;

      identifier = {
        ...identifier,
        ulis: [{ uli, type, compartmentId }],
        slot: slotId ? { slotId, compartmentId: allocatedCompartmentId ?? compartmentId } : null,
      };
    }

    if (actionBrief.actionOrigin === AllocationViewActionIdentifierTypeEnum.Slot) {
      const { uli, type, compartmentId, uliCompartmentId, slotId, truckId: slotTruckId } = actionBrief;

      const uliInfo = !!uli
        ? [
            {
              uli,
              type,
              compartmentId: uliCompartmentId ?? compartmentId,
            },
          ]
        : [];
      identifier = {
        ...identifier,
        truckId: slotTruckId,
        ulis: uliInfo,
        slot: { slotId, compartmentId },
      };
    }

    this.store.dispatch(new AllocationViewSetActionIdentifier({ identifier, type: actionBrief.actionOrigin }));
  }

  completeAction(): void {
    this._actionCompleted$.next();
    this.store.dispatch(new AllocationViewActionsCloseActionContainer());
  }

  closeAction(): void {
    this.store.dispatch(new AllocationViewActionsCloseActionContainer());
  }

  setInitialState(): void {
    this.store.dispatch(new AllocationViewActionsInitialState());
  }
}
